import axios from 'axios';

// Get API base URL from environment
const getApiBaseUrl = () => {
  return window.ENV?.VITE_API_BASE_URL || 'http://localhost:8000';
};

// Create axios instance
const api = axios.create({
  baseURL: `${getApiBaseUrl()}/api/v1`,
  timeout: 60000, // Increased to 60 seconds for Cloud Run cold starts
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Don't auto-logout during payment creation to avoid interrupting the flow
      const isPaymentRequest = error.config?.url?.includes('/payments/');
      
      if (!isPaymentRequest) {
        // Token expired or invalid - emit event for auto sign-in attempt
        window.dispatchEvent(new CustomEvent('authTokenExpired'));
        
        // Clear expired token
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        
        // Only redirect to home if not on a protected route that might benefit from auto sign-in
        const currentPath = window.location.pathname;
        const protectedPaths = ['/bookings', '/profile', '/admin'];
        const isProtectedPath = protectedPaths.some(path => currentPath.startsWith(path));
        
        if (!isProtectedPath) {
          window.location.href = '/';
        }
      } else {
        // Payment authentication error - don't auto-logout
      }
    } else if (error.response?.status === 403) {
      // Only handle 403 for authenticated users (account inactive)
      const hasAuthToken = localStorage.getItem('authToken');
      if (hasAuthToken) {
        // Account inactive - clear auth and store error message for after redirect
        const errorMessage = error.response?.data?.detail || 'Account is inactive. Please contact support.';
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        
        // Store error message to show after redirect
        localStorage.setItem('inactiveAccountError', errorMessage);
        
        // Redirect to home
        window.location.href = '/';
      }
      // For unauthenticated users, let the 403 error bubble up to be handled by the calling code
    }
    return Promise.reject(error);
  }
);

// API service class
class ApiService {
  constructor() {
    this.api = api;
  }

  setAuthToken(token) {
    // The request interceptor should handle the Authorization header automatically
    // This method exists for compatibility but doesn't need to do anything special
    // since the interceptor reads from localStorage
  }

  // Generic HTTP methods
  async get(url, config = {}) {
    return this.api.get(url, config);
  }

  async post(url, data = {}, config = {}) {
    return this.api.post(url, data, config);
  }

  async put(url, data = {}, config = {}) {
    return this.api.put(url, data, config);
  }

  async patch(url, data = {}, config = {}) {
    return this.api.patch(url, data, config);
  }

  async delete(url, config = {}) {
    return this.api.delete(url, config);
  }

  // Auth endpoints
  async googleLogin(token) {
    return this.post('/auth/google-login', { token });
  }

  // Vehicle endpoints
  async lookupVehicle(registration, { refreshMot = false } = {}) {
    return this.post('/vehicles/lookup', {
      registration_number: registration,
      ...(refreshMot && { refresh_mot: true }),
    });
  }

  // Garage endpoints
  async searchGarages(postcode, limit = 5, offset = 0, productIds = [], addonIds = []) {
    return this.post('/garages/search', {
      postcode: postcode,
      limit: limit,
      offset: offset,
      radius_miles: 5, // Fixed 5-mile radius
      product_ids: productIds,
      addon_ids: addonIds
    });
  }

  async getGarages(params = {}) {
    return this.get('/garages', { params });
  }

  async getMyGarages() {
    return this.get('/garages/admin/my-garages');
  }

  async getGarage(garageId) {
    return this.get(`/garages/${garageId}`);
  }

  async getGarageAvailability(garageId, date, options = {}) {
    const requestData = { 
      start_date: date,
      end_date: date,  // Same date for single day availability
      product_ids: options.product_ids || [],
      addon_ids: options.addon_ids || []
    };
    return this.post(`/garages/${garageId}/availability`, requestData);
  }

  async getAvailableProducts() {
    return this.get('/products');
  }

  async getAvailableAddons() {
    return this.get('/addons');
  }

  async getPostcodeCoordinates(postcode) {
    return this.get(`/garages/postcode/${postcode}/coordinates`);
  }

  async calculateGaragePricing(garageId, productIds, addonIds = []) {
    return this.post('/garages/pricing', {
      garage_id: garageId,
      product_ids: productIds,
      addon_ids: addonIds
    });
  }

  // Booking endpoints
  async createBooking(bookingData) {
    return this.post('/bookings', bookingData);
  }

  async getBookings(params = {}) {
    // Ensure arrays are properly serialized for FastAPI
    const serializedParams = { ...params };
    
    // Convert arrays to repeated query parameters
    ['customer_ids', 'product_ids', 'addon_ids'].forEach(key => {
      if (serializedParams[key] && Array.isArray(serializedParams[key])) {
        // Keep as array - axios will handle this correctly for FastAPI
        // FastAPI expects repeated params: ?product_ids=id1&product_ids=id2
      }
    });
    
    return this.get('/bookings', { 
      params: serializedParams,
      paramsSerializer: {
        indexes: null // This ensures arrays are serialized as repeated params
      }
    });
  }

  async getBooking(bookingId) {
    return this.get(`/bookings/${bookingId}`);
  }

  async updateBooking(bookingId, updates) {
    return this.patch(`/bookings/${bookingId}`, updates);
  }

  async updateBookingStatus(bookingId, status) {
    return this.patch(`/bookings/${bookingId}`, { status });
  }

  async cancelBooking(bookingId, reason = null) {
    if (reason) {
      // If reason provided, first update the notes, then cancel
      await this.patch(`/bookings/${bookingId}`, { notes: reason });
    }
    return this.delete(`/bookings/${bookingId}`);
  }

  async checkBookingConflicts(conflictData) {
    return this.post('/bookings/check-conflicts', conflictData);
  }

  // Payment endpoints
  async createPaymentSession(bookingData) {
    return this.post('/payments/create-checkout-session', bookingData);
  }

  async createStripeSession(paymentData) {
    return this.post('/payments/create-stripe-session', paymentData);
  }

  async createCheckoutSession(sessionData) {
    return this.post('/payments/create-checkout-session', sessionData);
  }

  async getPaymentStatus(sessionId) {
    return this.get(`/payments/status/session/${sessionId}`);
  }

  async getPaymentStatusByBooking(bookingId) {
    return this.get(`/payments/status/${bookingId}`);
  }

  async updatePaymentStatus(bookingId, status) {
    return this.patch(`/bookings/${bookingId}/payment-status`, { payment_status: status });
  }

  // ================================
  // ADDITIONAL METHODS FROM apiClient.js
  // ================================

  /**
   * Get products with filtering
   * @param {Object} params - Query parameters
   * @param {string} params.status - Filter by status (active, inactive)
   * @param {string} params.category - Filter by category
   */
  async getProducts(params = {}) {
    return this.get('/products', { params });
  }

  /**
   * Get specific product
   * @param {string} productId - Product ID
   */
  async getProduct(productId) {
    return this.get(`/products/${productId}`);
  }

  /**
   * Create product (super user only)
   * @param {Object} data - Product data
   */
  async createProduct(data) {
    return this.post('/products', data);
  }

  /**
   * Update product (super user only)
   * @param {string} productId - Product ID
   * @param {Object} data - Update data
   */
  async updateProduct(productId, data) {
    return this.put(`/products/${productId}`, data);
  }

  /**
   * Delete product (super user only)
   * @param {string} productId - Product ID
   */
  async deleteProduct(productId) {
    return this.delete(`/products/${productId}`);
  }

  /**
   * Get addons with filtering
   * @param {Object} params - Query parameters
   * @param {string} params.status - Filter by status (active, inactive)
   * @param {string} params.category - Filter by category
   */
  async getAddons(params = {}) {
    return this.get('/addons', { params });
  }

  /**
   * Get specific addon
   * @param {string} addonId - Addon ID
   */
  async getAddon(addonId) {
    return this.get(`/addons/${addonId}`);
  }

  /**
   * Create addon (super user only)
   * @param {Object} data - Addon data
   */
  async createAddon(data) {
    return this.post('/addons', data);
  }

  /**
   * Update addon (super user only)
   * @param {string} addonId - Addon ID
   * @param {Object} data - Update data
   */
  async updateAddon(addonId, data) {
    return this.put(`/addons/${addonId}`, data);
  }

  /**
   * Delete addon (super user only)
   * @param {string} addonId - Addon ID
   */
  async deleteAddon(addonId) {
    return this.delete(`/addons/${addonId}`);
  }

  /**
   * Get garage settings
   * @param {string} garageId - Garage ID
   */
  async getGarageSettings(garageId) {
    return this.get(`/garages/${garageId}/settings`);
  }

  /**
   * Update garage status
   * @param {string} garageId - Garage ID
   * @param {Object} statusUpdate - Status update data
   */
  async updateGarage(garageId, statusUpdate) {
    return this.put(`/garages/${garageId}/status`, statusUpdate);
  }

  /**
   * Update garage settings
   * @param {string} garageId - Garage ID
   * @param {Object} settings - Settings data
   */
  async updateGarageSettings(garageId, settings) {
    return this.put(`/garages/${garageId}/settings`, settings);
  }

  /**
   * Get garage admins
   * @param {string} garageId - Garage ID
   */
  async getGarageAdmins(garageId) {
    return this.get(`/garages/${garageId}/admins`);
  }

  /**
   * Create garage admin
   * @param {string} garageId - Garage ID
   * @param {Object} adminData - Admin data (user_email, name, is_admin)
   */
  async createGarageAdmin(garageId, adminData) {
    return this.post(`/garages/${garageId}/admins`, adminData);
  }

  /**
   * Update garage admin
   * @param {string} garageId - Garage ID
   * @param {string} adminId - Admin ID
   * @param {Object} data - Update data (name, is_admin, is_active)
   */
  async updateGarageAdmin(garageId, adminId, data) {
    return this.put(`/garages/${garageId}/admins/${adminId}`, data);
  }

  /**
   * Register garage admin
   * @param {Object} data - Admin registration data
   */
  async registerGarageAdmin(data) {
    return this.post('/garage-admins', data);
  }

  /**
   * Get users with filtering
   * @param {Object} params - Query parameters
   * @param {string} params.role - Filter by role
   * @param {string} params.status - Filter by status (active, inactive)
   */
  async getUsers(params = {}) {
    return this.get('/users', { params });
  }

  /**
   * Update user (super user only)
   * @param {string} userId - User ID
   * @param {Object} data - Update data (name, role, is_active)
   */
  async updateUser(userId, data) {
    return this.put(`/users/${userId}`, data);
  }

  // ================================
  // CONVENIENCE METHODS
  // ================================

  /**
   * Get active products only
   */
  async getActiveProducts() {
    return this.getProducts({ status: 'active' });
  }

  /**
   * Get inactive products only
   */
  async getInactiveProducts() {
    return this.getProducts({ status: 'inactive' });
  }

  /**
   * Get active addons only
   */
  async getActiveAddons() {
    return this.getAddons({ status: 'active' });
  }

  /**
   * Get inactive addons only
   */
  async getInactiveAddons() {
    return this.getAddons({ status: 'inactive' });
  }

  /**
   * Get active garages only
   */
  async getActiveGarages() {
    return this.getGarages({ status: 'active' });
  }

  /**
   * Get inactive garages only
   */
  async getInactiveGarages() {
    return this.getGarages({ status: 'inactive' });
  }

  /**
   * Search garages by query (for admin interface)
   * @param {string} query - Search query
   * @param {Object} options - Additional options
   */
  async searchGaragesByQuery(query, options = {}) {
    const response = await this.getGarages({ query: query, ...options });
    return response;
  }

  /**
   * Get products by category
   * @param {string} category - Product category
   */
  async getProductsByCategory(category) {
    return this.getProducts({ category });
  }

  /**
   * Get addons by category
   * @param {string} category - Addon category
   */
  async getAddonsByCategory(category) {
    return this.getAddons({ category });
  }

  /**
   * Get users by role
   * @param {string} role - User role
   */
  async getUsersByRole(role) {
    return this.getUsers({ role });
  }

  /**
   * Get garage bookings
   * @param {string} garageId - Garage ID
   * @param {Object} params - Additional query parameters
   */
  async getGarageBookings(garageId, params = {}) {
    return this.getBookings({ garage_id: garageId, ...params });
  }

  /**
   * Get user bookings
   * @param {string} userId - User ID
   */
  async getUserBookings(userId) {
    return this.getBookings({ user_id: userId });
  }

  // ========================================
  // GARAGE CUSTOMERS ENDPOINTS
  // ========================================

  /**
   * Get garage customers
   * @param {string} garageId - Garage ID
   * @param {Object} params - Query parameters (search, etc.)
   */
  async getGarageCustomers(garageId, params = {}) {
    return api.get(`/garage/${garageId}/customers`, { params });
  }

  /**
   * Create garage customer
   * @param {string} garageId - Garage ID
   * @param {Object} customerData - Customer data
   */
  async createGarageCustomer(garageId, customerData) {
    return api.post(`/garage/${garageId}/customers`, customerData);
  }

  /**
   * Get specific garage customer
   * @param {string} garageId - Garage ID
   * @param {string} customerId - Customer ID
   */
  async getGarageCustomer(garageId, customerId) {
    return api.get(`/garage/${garageId}/customers/${customerId}`);
  }

  /**
   * Update garage customer
   * @param {string} garageId - Garage ID
   * @param {string} customerId - Customer ID
   * @param {Object} customerData - Updated customer data
   */
  async updateGarageCustomer(garageId, customerId, customerData) {
    return api.put(`/garage/${garageId}/customers/${customerId}`, customerData);
  }

  /**
   * Fetch the latest MOT expiry from DVLA for a garage customer's vehicle
   * @param {string} garageId - Garage ID
   * @param {string} customerId - Customer ID
   */
  async refreshGarageCustomerMot(garageId, customerId) {
    return api.post(`/garage/${garageId}/customers/${customerId}/refresh-mot`);
  }

  /**
   * Delete garage customer
   * @param {string} garageId - Garage ID
   * @param {string} customerId - Customer ID
   */
  async deleteGarageCustomer(garageId, customerId) {
    return api.delete(`/garage/${garageId}/customers/${customerId}`);
  }
}

export const apiService = new ApiService();
export default apiService;